<template>
    <div>
        <template v-if="items.length">
            <!-- Antragsteller/Versicherungsnehmer -->
            <div class="mb-4">
                <div class="font-xl-links-bold mb-3">
                    Antragsteller/Versicherungsnehmer
                </div>
                <div class="font-xl-links">
                    <template v-if="hasVN">
                        <template v-if="vnPerson.vorname && vnPerson.nachname">
                            {{ vnPerson.vorname }} {{ vnPerson.nachname }}
                            <template v-if="vnPerson.geburtstag && isValidGeburtstag(vnPerson.geburtstag)">
                                (geb. {{ formatiertDatum(vnPerson.geburtstag) }})
                            </template>
                            <template v-else>
                                (bitte das Geburtsdatum angeben)
                            </template>
                        </template>
                        <template v-else>
                            Bitte ergänzen Sie den Namen
                        </template>
                    </template>
                    <template v-else>
                        Im folgenden Schritt ergänzen Sie die Angaben zum Antragsteller/Versicherungsnehmer.
                    </template>
                </div>
            </div>

            <!-- Überschrift Versicherte Person(en) -->
            <div class="font-xl-links-bold mb-3">
                {{ items.length === 1 ? 'Versicherte Person' : 'Versicherte Personen' }}
            </div>

            <!-- Versicherte Personen mit AR20-Beiträgen -->
            <div v-for="(item, index) in items" :key="item.id" :class="{ 'mb-3': index !== items.length - 1 }">
                <div class="font-xl-links">
                    <template v-if="item.vorname && item.nachname">
                        {{ item.vorname }} {{ item.nachname }}
                        <template v-if="item.geburtstag && isValidGeburtstag(item.geburtstag)">
                            (geb. {{ formatiertDatum(item.geburtstag) }})
                        </template>
                        <template v-else>
                            (bitte das Geburtsdatum angeben)
                        </template>
                    </template>
                    <template v-else>
                        {{ index + 1 }}. Person: Bitte ergänzen Sie den Namen
                    </template>
                </div>
                
                <!-- AR20 Beiträge pro Person -->
                <div class="row mt-3" v-if="item.tarif6 === 1">
                    <!-- Tarifname -->
                    <div class="col-12 col-md-6 font-xl-links">
                        Ausland (AR20)
                    </div>
                    <!-- Hinweis bei ungültigem Geburtsdatum -->
                    <div v-if="!isValidGeburtstag(item.geburtstag)" class="col-12 col-md-6 d-flex flex-column align-items-md-end text-muted">
                        Bitte geben Sie das Geburtsdatum ein.
                    </div>
                    <!-- Erstbeitrag und Jahresbeitrag untereinander (nur bei gültigem Geburtsdatum) -->
                    <div v-else class="col-12 col-md-6 d-flex flex-column align-items-md-end">
                        <div class="beitrag-zeile">
                            <span class="beitrag-label">Erstbeitrag ({{ getMindestlaufzeitText(item) }}):</span>
                            <strong class="beitrag-wert">{{ getErstbeitragStr(item) }}</strong>
                        </div>
                        <div class="beitrag-zeile">
                            <span class="beitrag-label">Jahresbeitrag (ab {{ getJahresbeitragStartStr() }}):</span>
                            <strong class="beitrag-wert">{{ getJahresbeitragStr(item) }}</strong>
                        </div>
                    </div>
                </div>
                <hr v-if="index !== items.length - 1">
            </div>
            
            <!-- Horizontaler Trenner mit Abstand vor Gesamtbeitrag -->
            <hr class="mt-4">
            
            <!-- Gesamtbeitrag -->
            <div class="row mb-2">
                <div class="col-12 col-md-6 d-flex align-items-center">
                    <div class="font-xl-links-bold">Gesamt Erstbeitrag</div> 
                    <div class="ms-2"><MindestvertragsdauerPopover /></div>
                </div>
                <div class="col-12 col-md-6 font-xl-rechts-bold" :class="{ 'text-muted': !alleGeburtsdatenGueltig }">
                    {{ gesamtErstbeitragStr }}
                </div>
            </div>
            
            <!-- Hinweis zur automatischen Verlängerung (nur wenn alle Daten gültig) -->
            <div v-if="alleGeburtsdatenGueltig" class="mt-3 font-base-links text-muted">
                * bei automatischer Verlängerung um 12 Monate beträgt der Folgebeitrag in Summe {{ gesamtJahresbeitragStr }}
            </div>
        </template>
        <template v-else>
            <div class="font-xl-links text-center py-4">
                Bitte fügen Sie zu versichernde Personen hinzu.
            </div>
        </template>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { useRechnerStore } from '@/stores/rechner';
import { 
    formatiertDatum, 
    isValidDateInPast,
    berechneMindestlaufzeit,
    berechneJahresbeitragStart,
    berechneAR20Beitraege,
    formatiereBeitrag
} from '@/helpers/hilfsfunktionen';
import MindestvertragsdauerPopover from '@/components/ar20/ar20_mindestvertragsdauer_popover.vue';

const props = defineProps({
    items: {
        type: Array,
        default: () => []
    },
    vndaten: {
        type: Object,
        default: () => ({ id: 'initial' })
    }
});

const store = useRechnerStore();

// Prüft, ob eine Person als VN ausgewählt wurde
const hasVN = computed(() => {
    return props.vndaten.id !== 'initial' && props.vndaten.id !== 'vn0';
});

// Findet die VN-Person in den Items (falls ausgewählt)
const vnPerson = computed(() => {
    if (!hasVN.value) {
        return null;
    }
    return props.items.find(item => item.id === props.vndaten.id) || props.vndaten;
});

const isValidGeburtstag = (datum) => {
    return datum && isValidDateInPast(datum);
};

// Mindestlaufzeit-Text für eine Person
const getMindestlaufzeitText = (item) => {
    const ml = berechneMindestlaufzeit(store.versicherungsbeginn);
    if (ml.monate > 0) {
        return `${ml.vonStr} - ${ml.bisStr}`;
    }
    return '';
};

// Jahresbeitrag-Start für alle (gleich)
const getJahresbeitragStartStr = () => {
    return berechneJahresbeitragStart(store.versicherungsbeginn);
};

// Erstbeitrag für eine Person
const getErstbeitragStr = (item) => {
    if (!item.tarif6beitrag || item.tarif6beitrag <= 0) {
        return '- €';
    }
    const beitraege = berechneAR20Beitraege(item.tarif6beitrag, store.versicherungsbeginn);
    return formatiereBeitrag(beitraege.erstbeitrag);
};

// Jahresbeitrag für eine Person
const getJahresbeitragStr = (item) => {
    if (!item.tarif6beitrag || item.tarif6beitrag <= 0) {
        return '- €';
    }
    return formatiereBeitrag(item.tarif6beitrag);
};

// Prüft ob alle Personen ein gültiges Geburtsdatum haben
const alleGeburtsdatenGueltig = computed(() => {
    return props.items.every(item => item.tarif6 !== 1 || isValidGeburtstag(item.geburtstag));
});

// Gesamt-Erstbeitrag (nur für Personen mit gültigem Geburtsdatum)
const gesamtErstbeitrag = computed(() => {
    let summe = 0;
    props.items.forEach(item => {
        if (item.tarif6 === 1 && item.tarif6beitrag > 0 && isValidGeburtstag(item.geburtstag)) {
            const beitraege = berechneAR20Beitraege(item.tarif6beitrag, store.versicherungsbeginn);
            summe += beitraege.erstbeitrag;
        }
    });
    return summe;
});

const gesamtErstbeitragStr = computed(() => {
    if (!alleGeburtsdatenGueltig.value) {
        return 'Bitte alle Geburtsdaten eingeben';
    }
    return formatiereBeitrag(gesamtErstbeitrag.value);
});

// Gesamt-Jahresbeitrag (Folgebeitrag, nur für Personen mit gültigem Geburtsdatum)
const gesamtJahresbeitrag = computed(() => {
    let summe = 0;
    props.items.forEach(item => {
        if (item.tarif6 === 1 && item.tarif6beitrag > 0 && isValidGeburtstag(item.geburtstag)) {
            summe += item.tarif6beitrag;
        }
    });
    return summe;
});

const gesamtJahresbeitragStr = computed(() => {
    if (!alleGeburtsdatenGueltig.value) {
        return '- €';
    }
    return formatiereBeitrag(gesamtJahresbeitrag.value);
});
</script>

<style scoped>
.font-xl-links-bold {
    font-weight: 700;
}

.font-xl-rechts-bold {
    font-weight: 700;
    text-align: right;
}

@media (max-width: 767px) {
    .font-xl-rechts-bold {
        text-align: left;
    }
}

.beitrag-zeile {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 0.25rem;
}

.beitrag-label {
    font-size: 0.9rem;
}

.beitrag-wert {
    font-size: 1rem;
}

@media (min-width: 768px) {
    .beitrag-zeile {
        flex-direction: row;
        gap: 0.5rem;
        margin-bottom: 0;
        align-items: flex-end;
    }
    
    .beitrag-label {
        font-size: 1rem;
    }
}
</style>
